import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GradeLevel, Subject } from 'src/services/services/parent/parent.api';
import { showNotification } from '@mantine/notifications';
import { NotificationTypes } from 'src/enums/Notifications';
import { ChatMessage, FlaggedData } from 'src/interfaces/Chat';
import { ChildCreationData, ChildUpdationData } from 'src/interfaces/Children';
import { ResponseKind } from 'src/services/services/MainService';
import { parentService } from 'src/services/services/parent/parent.api';
import { userService } from 'src/services/services/user/user.api';
import {
  sendFailureNotification,
  sendSuccessNotification,
} from 'src/utils/notifications';
import {
  setChildren,
  addChild,
  updateChild as updateChildAction,
  removeChild,
  setChildMessages,
  setChildSessions,
  setSelectedSession,
  setChatLoading,
  setSelectedChild,
  setInitialized,
  setGradeLevels,
  setSubjects,
  setLoadingGradeLevels,
  setLoadingSubjects,
  setActiveSubject,
} from 'src/redux/redux-store/children/childrenReducer';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import { UserRole } from 'src/enums/User';
const useChildrenData = () => {
  const dispatch = useAppDispatch();
  const {
    children,
    childMessages,
    childSessions,
    selectedSession,
    initialized,
    chatLoading,
    selectedChild,
    gradeLevels,
    subjects,
    activeSubject,
  } = useAppSelector((state) => state.child);
  const userRole = useAppSelector((state) => state.user.role);
  const [loading, setLoading] = useState(false);
  const [loadingGradeLevels, setLoadingGradeLevels] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string | undefined>(
    undefined
  );
  // Define getChildren with useCallback to avoid dependency issues
  const getChildren = useCallback(async () => {
    // Only fetch children if user is a parent
    if (userRole !== UserRole.parent) {
      return;
    }
    const response = await parentService.getChildren();
    console.log('get children response');
    if (response.kind === ResponseKind.OK && response.data) {
      dispatch(setChildren(response.data.children));
      dispatch(setInitialized(true));
    }
  }, [dispatch, userRole]);
  // Fetch grade levels
  const fetchGradeLevels = useCallback(async () => {
    // Only fetch grade levels if user is a parent
    if (userRole !== UserRole.parent) {
      return;
    }
    try {
      dispatch({ type: 'children/setLoadingGradeLevels', payload: true });
      const response = await parentService.getGradeLevels();
      console.log('response from grade level api : ', response);
      if (response.kind === ResponseKind.OK && response.data) {
        dispatch({ type: 'children/setGradeLevels', payload: response.data });
      }
    } catch (error) {
      console.error('Error fetching grade levels:', error);
      showNotification({
        message: 'Failed to load grade levels',
        color: 'red',
      });
    } finally {
      dispatch({ type: 'children/setLoadingGradeLevels', payload: false });
    }
  }, [dispatch, userRole]);
  // Fetch subjects for a specific grade level
  const fetchSubjectsByGrade = useCallback(
    async (gradeId: string) => {
      // Only fetch subjects by grade if user is a parent
      if (userRole !== UserRole.parent) {
        return;
      }
      if (!gradeId) {
        dispatch({ type: 'children/setSubjects', payload: [] });
        return;
      }
      try {
        dispatch({ type: 'children/setLoadingSubjects', payload: true });
        const response = await parentService.getSubjectsByGrade(gradeId);
        if (response.kind === ResponseKind.OK && response.data) {
          dispatch({ type: 'children/setSubjects', payload: response.data });
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
        showNotification({
          message: 'Failed to load subjects',
          color: 'red',
        });
      } finally {
        dispatch({ type: 'children/setLoadingSubjects', payload: false });
      }
    },
    [dispatch, userRole]
  );
  // Load initial data - only for parent accounts
  useEffect(() => {
    if (userRole === UserRole.parent) {
      getChildren();
      fetchGradeLevels();
    }
  }, [getChildren, fetchGradeLevels, userRole]);
  // Function to fetch child's subjects
  const fetchChildSubjects = useCallback(async () => {
    if (!selectedChild) return;
    try {
      const subjectsResponse = await userService.getAllSubjects(selectedChild);
      if (subjectsResponse.kind === ResponseKind.OK && subjectsResponse.data) {
        const subjectNames = subjectsResponse.data.map((item: any) =>
          typeof item === 'string' ? item : item.name
        );
        dispatch(setSubjects(subjectNames));
        // Auto-select the first subject if available
        if (subjectNames.length > 0) {
          dispatch({
            type: 'children/setActiveSubject',
            payload: subjectNames[0],
          });
        } else {
          dispatch({ type: 'children/setActiveSubject', payload: '' });
        }
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      showNotification({
        message: 'Failed to load subjects. Please try again.',
        color: 'red',
      });
      dispatch({ type: 'children/setActiveSubject', payload: '' });
    }
  }, [selectedChild, dispatch]);
  // Function to fetch child's sessions
  const fetchChildSessions = useCallback(async () => {
    if (
      !selectedChild ||
      !activeSubject ||
      activeSubject === '' ||
      !subjects ||
      subjects.length === 0
    ) {
      dispatch({ type: 'children/setChildSessions', payload: [] });
      dispatch({ type: 'children/setSelectedSession', payload: '' });
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    if (!selectedChild) {
      dispatch(setChildSessions([]));
      dispatch(setSelectedSession(''));
      dispatch(setChildMessages([]));
      return;
    }
    try {
      const sessionsResponse = await parentService.getChildSessions(
        selectedChild,
        activeSubject || undefined,
        selectedLevel
      );
      if (sessionsResponse.kind === ResponseKind.OK && sessionsResponse.data) {
        const sessions = sessionsResponse.data.chat_sessions;
        dispatch(setChildSessions(sessions));
        // Auto-select the first session if there are any sessions available
        if (sessions && sessions.length > 0) {
          const firstSession = sessions[0];
          dispatch(setSelectedSession(firstSession.id));
          // Also fetch messages for the first session
          try {
            const messagesResponse = await parentService.getChildChat(
              selectedChild,
              firstSession.id
            );
            if (
              messagesResponse.kind === ResponseKind.OK &&
              messagesResponse.data
            ) {
              dispatch(setChildMessages(messagesResponse.data.messages));
            }
          } catch (error) {
            console.error(
              'Error fetching messages for auto-selected session:',
              error
            );
          }
        } else {
          // No sessions available, clear the selected session and messages
          dispatch(setSelectedSession(''));
          dispatch(setChildMessages([]));
        }
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      showNotification({
        message: 'Failed to load sessions. Please try again.',
        color: 'red',
      });
    }
  }, [selectedChild, activeSubject, dispatch]);
  // Fetch subjects and sessions when selected child changes
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    const resetState = () => {
      if (!isMounted) return;
      dispatch({ type: 'children/setActiveSubject', payload: '' });
      dispatch({ type: 'children/setChildSessions', payload: [] });
      dispatch({ type: 'children/setSelectedSession', payload: '' });
      dispatch({ type: 'children/setChildMessages', payload: [] });
      dispatch({ type: 'children/setSubjects', payload: [] });
    };
    const loadData = async () => {
      if (!selectedChild) {
        resetState();
        return;
      }
      // Reset state first to prevent showing stale data
      resetState();
      try {
        // Fetch subjects for the selected child
        await fetchChildSubjects();
      } catch (error: unknown) {
        if (error instanceof Error) {
          if (error.name !== 'AbortError') {
            console.error('Error in loadData:', error);
            showNotification({
              title: 'Error',
              message: 'Failed to load data. Please try again.',
              color: 'red',
              autoClose: 5000,
            });
          }
        } else {
          console.error('An unknown error occurred:', error);
        }
      }
    };
    loadData();
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [selectedChild, fetchChildSubjects, dispatch]);
  // Fetch sessions when active subject or selected child changes
  const lastLoadedRef = useRef({
    child: '',
    subject: '',
  });

  // Fetch sessions when active subject or selected child changes
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const loadSessions = async () => {
      if (!isMounted) return;

      // Clear sessions immediately when child changes or when no subject is selected
      if (!selectedChild || !activeSubject) {
        if (isMounted) {
          dispatch({ type: 'children/setChildSessions', payload: [] });
          dispatch({ type: 'children/setSelectedSession', payload: '' });
          lastLoadedRef.current = { child: '', subject: '' };
        }
        return;
      }

      // Only proceed if both child and subject are selected
      if (selectedChild && activeSubject) {
        // Skip if we've already loaded sessions for this exact child and subject
        if (
          lastLoadedRef.current.child === selectedChild &&
          lastLoadedRef.current.subject === activeSubject
        ) {
          return;
        }

        try {
          // Clear current sessions while loading new ones
          dispatch({ type: 'children/setChildSessions', payload: [] });
          dispatch({ type: 'children/setSelectedSession', payload: '' });

          await fetchChildSessions();

          // Update the last loaded ref after successful load
          if (isMounted) {
            lastLoadedRef.current = {
              child: selectedChild,
              subject: activeSubject,
            };
          }
        } catch (error: unknown) {
          if (
            error instanceof Error &&
            error.name !== 'AbortError' &&
            isMounted
          ) {
            console.error('Error fetching sessions:', error);
            showNotification({
              message: 'Failed to load sessions. Please try again.',
              color: 'red',
              autoClose: 5000,
            });
            // Reset last loaded on error
            lastLoadedRef.current = { child: '', subject: '' };
          }
        }
      }
    };

    // Use a small delay to batch rapid changes
    const timer = setTimeout(loadSessions, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      abortController.abort();
    };
  }, [
    activeSubject,
    selectedChild,
    fetchChildSessions,
    selectedLevel,
    dispatch,
  ]);

  const createChild = async (
    data: ChildCreationData,
    profile_pic: File | null
  ) => {
    try {
      setLoading(true);
      const response = await parentService.createChild(data, profile_pic);
      console.log('response : ', response);

      if (response.kind === ResponseKind.OK && response.data) {
        dispatch(addChild(response.data));
        showNotification({
          message: 'Child created successfully',
          color: 'green',
        });
        return true;
      } else if ('errors' in response && response.errors) {
        const errorMessage =
          response.errors?.[0]?.message ||
          response.errors ||
          'Failed to create child';
        sendFailureNotification(
          NotificationTypes.childCreation,
          response.errors
        );
        throw new Error(errorMessage);
      }
      // Handle case where response.kind is neither OK nor "rejected"
      throw new Error('Unexpected response from server');
    } catch (e) {
      // Re-throw the error so it can be handled by the calling component
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const setChildPreference = async (preference: number) => {
    const response = await parentService.setChildPreference(preference);

    if (response.kind === ResponseKind.OK && response.data) {
      // getChildren();
      showNotification({
        message: 'Preference set successfully',
        color: 'green',
      });
      return true;
    } else if (response.kind === 'rejected') {
      sendFailureNotification(
        NotificationTypes.childPreference,
        response.errors
      );
      return false;
    }
  };

  const deleteChild = async (username: string) => {
    const response = await parentService.deleteChild(username);

    if (response.kind === ResponseKind.OK && response.data) {
      dispatch(removeChild(username));
      showNotification({
        message: 'Child deleted successfully',
        color: 'green',
      });
    } else {
      showNotification({
        message: 'Something went wrong',
        color: 'red',
      });
    }
  };

  const flagMessage = async (flagMessage: FlaggedData) => {
    const response = await parentService.flagMessage(flagMessage);
    if (response.kind === ResponseKind.OK) {
      sendSuccessNotification(
        NotificationTypes.flagMessage,
        'Your complaint was registered, you will hear shortly from our team!'
      );
    } else {
      sendFailureNotification(
        NotificationTypes.flagMessage,
        "Your complaint wasn't registered, please try again later!"
      );
    }
  };

  const getChildSessions = async (username: string) => {
    const response = await parentService.getChildSessions(username);

    if (response.kind === ResponseKind.OK && response.data) {
      dispatch(setChildSessions(response.data.chat_sessions));
    } else {
      showNotification({
        message: 'Something went wrong',
        color: 'red',
      });
    }
  };

  const updateChild = async (
    id: string,
    childData: Partial<ChildUpdationData>
  ) => {
    const response = await parentService.updateChild(id, childData);

    if (response.kind === ResponseKind.OK && response.data) {
      dispatch(updateChildAction({ id, data: childData }));
      getChildren();
      showNotification({
        message: 'Child updated successfully',
        color: 'green',
      });
      return true;
    } else if (response.kind === 'rejected') {
      sendFailureNotification(NotificationTypes.childUpdation, response.errors);
      return false;
    }
  };

  const onSelectSession = (id: string) => {
    dispatch(setSelectedSession(id));
  };

  const getChildMessages = async (username: string, sessionId: string) => {
    const response = await parentService.getChildChat(username, sessionId);

    if (response.kind === ResponseKind.OK && response.data) {
      const welcomeMessage: ChatMessage = {
        content: "Hi! Your child hasn't sent any messages yet.",
        role: 'ai',
        created_at: '',
      };
      const messages =
        response.data.messages.length === 0
          ? [welcomeMessage]
          : response.data.messages;
      dispatch(setChildMessages(messages));
    } else {
      showNotification({
        message: 'Something went wrong',
        color: 'red',
      });
    }
  };

  const setActiveSubject = useCallback(
    (subject: string | null) => {
      dispatch({ type: 'children/setActiveSubject', payload: subject });
    },
    [dispatch]
  );

  return {
    // State
    children,
    selectedSession,
    childSessions,
    childMessages,
    selectedChild,
    loading,
    gradeLevels,
    subjects,
    activeSubject,
    loadingGradeLevels,
    loadingSubjects,
    selectedLevel,

    // Actions
    createChild,
    deleteChild,
    updateChild,
    getChildMessages,
    onSelectSession,
    setChildPreference,
    setSelectedChild: (childId: string) => dispatch(setSelectedChild(childId)),
    flagMessage,

    // New actions
    fetchGradeLevels,
    fetchSubjectsByGrade,
    setActiveSubject,
    getChildren,
  };
};

export default useChildrenData;
